import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { ReactComponent as SendLogo } from "../../Assets/arrow-up-solid.svg";
import { AttachFileIcon, MicIcon } from "../AnimatedIcons/AnimatedIcons";
import {
  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE,
  isConversationAttachmentTooLarge,
} from "../../Helper/ConversationAttachmentUpload";
import { useConfigStore } from "../../Stores/Config";
import { isConversationRoute, useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";
import AudioRecorderUpload from "../AudioRecorderUpload/AudioRecorderUpload";
import { defaultAttachmentMessage } from "../ChatMessageComposer/ChatMessageComposer";
import { KaiVoiceButton } from "../KaiVoiceButton/KaiVoiceButton";
import ComposerShell from "../ComposerShell/ComposerShell";
import "./WidgetChatComposer.scss";

// Timing of the home → conversation takeoff: the composer glides down and
// morphs into the conversation composer (a 340ms transition in the SCSS),
// the typed text fades out just before the page swap (LIFT), and navigation
// to the conversation shell happens right after the composer has docked
// (TOTAL — kept a touch past the glide so the swap lands on a settled frame).
const AI_TAKEOFF_LIFT_DELAY_MS = 260;
const AI_TAKEOFF_TOTAL_MS = 380;

// Geometry of the docked composer — must match the conversation composer's
// real at-rest height, otherwise the shell reserves the wrong bottom space and
// the glide lands a few px off the docked composer (both read as a small jump
// as the takeoff settles). Measured: 1px top+bottom border + 50px textarea row
// + 50px toolbar row = 102px — the toolbar clears its 44px min-height because
// the 36px send button plus its 2px/12px padding is taller than that. The inset
// must equal the composer's --conversation-gutter (ThemeCss.ts): the glide is a
// transform to a point computed from it, so a stale value lands off the dock.
const DOCK_INSET = 16;
export const DOCKED_COMPOSER_HEIGHT = 102;

// Auto-grow bounds for the v4 hero textarea. The minimum matches the v4 rest
// row (and the docked conversation composer's 50px row the takeoff morphs
// into); past the maximum the content scrolls inside instead of the composer
// swallowing the home underneath.
const HERO_TEXTAREA_MIN_HEIGHT = 50;
const HERO_TEXTAREA_MAX_HEIGHT = 150;

// `--content-max-width` (index.scss): the tablet column the docked composer is
// capped to. Unset/unparseable (tests, storybook without the global styles)
// means "no cap".
const readContentMaxWidth = (): number => {
  if (typeof window === "undefined") {
    return Number.POSITIVE_INFINITY;
  }
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--content-max-width");
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : Number.POSITIVE_INFINITY;
};

type TakeoffState = {
  phase: "pre" | "glide" | "lift";
  top: number;
  left: number;
  width: number;
  height: number;
  targetWidth: number;
  dockDx: number;
  dockDy: number;
  text: string;
};

type AttachmentDescriptor = { url: string; name: string; type: string };

type QueueItem = {
  id: string;
  file: File;
  status: "uploading" | "done";
  progress: number;
  url?: string;
};

type WidgetChatComposerProps = {
  sendMessage: (
    message: string,
    opts: { navDelayMs: number; attachments?: AttachmentDescriptor[] },
  ) => void;
  disabled?: boolean;
  // "v4" renders the composer expanded at rest (matches the v4 hero design)
  // and enables the idle glow via config.homeComposerGlow (opt-in, off by
  // default). Default v3 pill.
  variant?: "v3" | "v4";
  // Fired when the input gains/loses focus — the home uses these to lift the
  // composer above the mobile keyboard and restore on dismiss.
  onFocus?: () => void;
  onBlur?: () => void;
};

// The home "ask me anything" composer. Structurally a clone of the
// conversation composer (same classes and DOM shape) so that, on send, it can
// glide down and expand into the exact composer the conversation shows — the
// page swap underneath then happens between two pixel-identical composers.
const WidgetChatComposer = ({
  sendMessage,
  disabled = false,
  variant = "v3",
  onFocus,
  onBlur,
}: WidgetChatComposerProps) => {
  const configStore = useConfigStore();
  const aiTakeoffActive = useTicketStore((store) => store.aiTakeoffActive);
  const [message, setMessage] = useState("");
  const [takeoff, setTakeoff] = useState<TakeoffState | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const [audioRecordingIsActive, setAudioRecordingIsActive] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const liftTimerRef = useRef<number | undefined>(undefined);
  const sendOnEnter = configStore.config?.sendOnEnter ?? true;
  const trimmedMessage = message.trim();
  const isV4 = variant === "v4";

  // Attachments are only interactive on the v4 hero composer (v3 keeps its
  // decorative, non-clickable toolbar). Vision gate mirrors the conversation.
  const showAttachmentDecor =
    configStore.config?.vision && !configStore.config?.disableAttachments;
  const attachmentsEnabled = isV4 && showAttachmentDecor;

  const hasUploading = queue.some((q) => q.status === "uploading");
  const readyAttachments = queue
    .filter((q) => q.status === "done" && q.url)
    .map(
      (q): AttachmentDescriptor => ({
        url: q.url as string,
        name: q.file.name,
        type: q.file.type,
      }),
    );
  const hasReadyAttachment = readyAttachments.length > 0;

  const canSend =
    !disabled &&
    !takeoff &&
    !hasUploading &&
    (trimmedMessage.length > 0 || hasReadyAttachment);
  const idleGlow =
    variant === "v4" && configStore.config?.homeComposerGlow === true;

  const fullPlaceholder = configStore.translateText("askMeAnything");
  const [placeholder, setPlaceholder] = useState("");

  useEffect(() => {
    setPlaceholder("");

    let currentIndex = 0;
    let intervalId: ReturnType<typeof setInterval>;

    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        if (currentIndex < fullPlaceholder.length) {
          setPlaceholder(fullPlaceholder.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(intervalId);
        }
      }, 40);
    }, 400);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [fullPlaceholder]);

  useEffect(() => {
    return () => {
      if (liftTimerRef.current) {
        clearTimeout(liftTimerRef.current);
      }
    };
  }, []);

  // Failure recovery: conversation creation failed while the composer was
  // mid-takeoff (the store resets aiTakeoffActive without navigating). Put
  // the composer back in place with the typed question restored.
  //
  // Guard on the router: the SUCCESS path ALSO flips aiTakeoffActive→false — the
  // store does it right after pushPage("conversationloading"). But App renders
  // the routed page from a DEFERRED copy of currentPage, so this home (and its
  // position:fixed composer clone) stay mounted for a frame or two after the nav.
  // Running the restore then tore the docked composer back to its in-flow hero
  // slot — it visibly rose off the bottom (the "input box looks cut off while it
  // animates down" report) before unmounting. Only restore when we're genuinely
  // still on home (a real pre-navigation failure); once we've pushed the takeoff
  // pages, leave the glide clone docked until it unmounts.
  useEffect(() => {
    const navigatedIntoConversation = isConversationRoute(
      useRouterStore.getState().currentPage,
    );
    if (!aiTakeoffActive && takeoff && !navigatedIntoConversation) {
      if (liftTimerRef.current) {
        clearTimeout(liftTimerRef.current);
      }
      setTakeoff(null);
      setMessage(takeoff.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTakeoffActive]);

  // Auto-grow the v4 hero textarea with its content (the v3 pill stays a
  // fixed single row). Same measure as the conversation composer: reset to
  // auto, read scrollHeight, clamp. On takeoff the inline height is handed back
  // to the CSS so the height tween can glide the grown row down to the docked
  // 50px (audioRecordingIsActive is a dep because leaving voice mode remounts
  // the textarea, losing its inline height).
  useEffect(() => {
    const el = textareaRef.current;
    if (!isV4 || !el) {
      return;
    }

    if (takeoff) {
      el.style.height = "";
      el.style.overflowY = "";
      return;
    }

    // `auto` (the rows=1 box), never 0px — a zero-height textarea makes iPad
    // WKWebView scroll the caret line out of view and the caret then renders
    // one line below the text (see ChatMessageComposer for the full story).
    el.style.height = "auto";
    const contentHeight = el.scrollHeight;
    el.style.height =
      Math.min(
        Math.max(contentHeight, HERO_TEXTAREA_MIN_HEIGHT),
        HERO_TEXTAREA_MAX_HEIGHT,
      ) + "px";
    // Only scroll inside once the cap is hit — below it the hero keeps its
    // overflow: hidden, so no scrollbar flashes mid-growth.
    el.style.overflowY =
      contentHeight > HERO_TEXTAREA_MAX_HEIGHT ? "auto" : "";
  }, [isV4, message, takeoff, audioRecordingIsActive]);

  // Upload a picked file, reflecting progress/url back into the queue. The
  // conversation composer's uploadFile is reused — no reimplementation.
  const startUpload = (id: string, file: File) => {
    useTicketStore
      .getState()
      .uploadFile(file, (pct) => {
        setQueue((prev) =>
          prev.map((q) => (q.id === id ? { ...q, progress: pct } : q)),
        );
      })
      .then((url) => {
        setQueue((prev) =>
          prev.map((q) =>
            q.id === id ? { ...q, status: "done", progress: 100, url } : q,
          ),
        );
      })
      .catch((err: any) => {
        console.warn("Attachment upload failed:", err);
        setAttachmentError(err?.message || "Upload failed. Please try again.");
        setQueue((prev) => prev.filter((q) => q.id !== id));
      });
  };

  const setFilesSmart = (files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }

    const filesToUpload = files.filter(
      (file) => !isConversationAttachmentTooLarge(file),
    );

    if (filesToUpload.length < files.length) {
      setAttachmentError(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE);
    } else {
      setAttachmentError(undefined);
    }

    if (filesToUpload.length === 0) {
      return;
    }

    const newItems: QueueItem[] = filesToUpload.map((file) => ({
      id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      file,
      status: "uploading",
      progress: 0,
      url: undefined,
    }));

    setQueue((prev) => [...prev, ...newItems]);
    newItems.forEach((item) => startUpload(item.id, item.file));
  };

  const removeQueued = (id: string) => {
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const handleFileInputClick = () => {
    var inputElement = document.createElement("input");
    inputElement.style.display = "none";
    if (document.body) {
      document.body.appendChild(inputElement);
    }
    inputElement.type = "file";
    inputElement.accept =
      ".json,.pdf,.csv,.txt,.xlsx,.mp4,.mp3,.mov,.webp,.webm,image/jpeg,image/png,image/jpg,image/webp,image/webm,image/gif,.zip,.log";
    inputElement.multiple = true;
    inputElement.addEventListener("change", () => {
      if (inputElement.files && inputElement.files.length > 0) {
        setFilesSmart(Array.from(inputElement.files));
      }
      if (inputElement.parentNode) {
        inputElement.remove();
      }
    });
    inputElement.dispatchEvent(new MouseEvent("click"));
  };

  // Attachment / audio sends skip the takeoff glide (the upload already took
  // time, so a plain page transition is correct). Clears the composer.
  const sendWithAttachments = (
    outgoing: string,
    attachments: AttachmentDescriptor[],
  ) => {
    setAttachmentError(undefined);
    sendMessage(outgoing, { navDelayMs: 0, attachments });
    setMessage("");
    setQueue([]);
  };

  const handleSend = () => {
    if (!canSend) {
      return;
    }

    // Attachment path: deliver text + attachments as one message, no glide.
    if (hasReadyAttachment) {
      sendWithAttachments(
        trimmedMessage.length > 0 ? trimmedMessage : defaultAttachmentMessage,
        readyAttachments,
      );
      return;
    }

    // Text-only path: the original takeoff choreography (unchanged).
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const el = composerRef.current;

    if (prefersReducedMotion || !el) {
      sendMessage(trimmedMessage, { navDelayMs: 0 });
      setMessage("");
      return;
    }

    const rect = el.getBoundingClientRect();
    // Docked geometry: full width minus the gutters, capped to the tablet
    // content column and centered — the same max(gutter, (100% - column)/2)
    // edges the conversation composer uses in ComposerShell.scss.
    const dockWidth = Math.min(
      window.innerWidth - DOCK_INSET * 2,
      readContentMaxWidth(),
    );
    const dockLeft = (window.innerWidth - dockWidth) / 2;
    setTakeoff({
      phase: "pre",
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      targetWidth: dockWidth,
      dockDx: dockLeft - rect.left,
      dockDy:
        window.innerHeight - DOCK_INSET - DOCKED_COMPOSER_HEIGHT - rect.top,
      text: trimmedMessage,
    });
    useTicketStore.setState({ aiTakeoffActive: true });

    // The conversation is created in parallel — only the navigation to the
    // loading shell waits for the choreography.
    sendMessage(trimmedMessage, { navDelayMs: AI_TAKEOFF_TOTAL_MS });

    // Two frames so the fixed start geometry is committed before the
    // transition targets are applied.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTakeoff((current) =>
          current && current.phase === "pre"
            ? { ...current, phase: "glide" }
            : current,
        );
      });
    });

    liftTimerRef.current = window.setTimeout(() => {
      setTakeoff((current) =>
        current && current.phase === "glide"
          ? { ...current, phase: "lift" }
          : current,
      );
    }, AI_TAKEOFF_LIFT_DELAY_MS);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (sendOnEnter) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    } else {
      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSend();
      }
    }
  };

  const takeoffStyle = takeoff
    ? {
        top: `${takeoff.top}px`,
        left: `${takeoff.left}px`,
        width: `${
          takeoff.phase === "pre" ? takeoff.width : takeoff.targetWidth
        }px`,
        transform:
          takeoff.phase === "pre"
            ? undefined
            : `translate(${takeoff.dockDx}px, ${takeoff.dockDy}px)`,
      }
    : undefined;

  // Hero wrapper modifier classes: takeoff phases (takeoff/glide/lift), the v4
  // expanded variant, and the idle glow. The base `chat-message-composer` class
  // is applied by the shell.
  const heroClassName = [
    "widget-hero-composer",
    takeoff ? "widget-hero-composer--takeoff" : "",
    takeoff && takeoff.phase !== "pre" ? "widget-hero-composer--glide" : "",
    takeoff?.phase === "lift" ? "widget-hero-composer--lift" : "",
    variant === "v4" ? "widget-hero-composer--v4" : "",
    idleGlow ? "widget-hero-composer--idle-glow" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {takeoff && (
        <div
          className="widget-hero-composer-ghost"
          style={{ height: takeoff.height }}
          aria-hidden="true"
        />
      )}
      <ComposerShell
        ref={composerRef}
        className={heroClassName}
        style={takeoffStyle}
        audioActive={audioRecordingIsActive}
        errorMessage={attachmentError}
        onDismissError={() => setAttachmentError(undefined)}
        attachments={queue}
        onRemoveAttachment={removeQueued}
        toolbarAriaHidden={!!takeoff}
        textarea={
          <textarea
            ref={textareaRef}
            rows={1}
            // v4 wraps + auto-grows; the v3 pill is a fixed single row, so
            // long text side-scrolls instead of wrapping out of view.
            wrap={isV4 ? "soft" : "off"}
            spellCheck={false}
            value={message}
            placeholder={placeholder}
            aria-label={fullPlaceholder}
            readOnly={!!takeoff}
            onFocus={() => onFocus?.()}
            onBlur={() => onBlur?.()}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
            onKeyDown={handleInputKeyDown}
          />
        }
      >
        {isV4 &&
          !audioRecordingIsActive &&
          queue.length === 0 &&
          trimmedMessage.length === 0 && <KaiVoiceButton newConversation />}
        {!audioRecordingIsActive &&
          showAttachmentDecor &&
          (attachmentsEnabled ? (
            <AttachFileIcon
              className="attachment-button"
              onClick={handleFileInputClick}
            />
          ) : (
            <AttachFileIcon className="attachment-button" />
          ))}
        {isV4 ? (
          queue.length > 0 ? null : (
            <AudioRecorderUpload
              onAudioRecordingStart={() => {
                setAudioRecordingIsActive(true);
              }}
              onAudioRecordingStop={() => {
                setAudioRecordingIsActive(false);
              }}
              onSend={(attachment) => {
                // The recorder uploads the recording itself and hands back a
                // ready descriptor — send it straight into a fresh
                // conversation (no text needed, no takeoff).
                sendWithAttachments(defaultAttachmentMessage, [attachment]);
              }}
            />
          )
        ) : (
          <MicIcon className="audio-recorder-icon" />
        )}
        {!audioRecordingIsActive && (
          <div
            onClick={handleSend}
            className={`send-button ${!canSend ? "send-button--disabled" : ""}`}
            role="button"
            aria-label={configStore.translateText("sendMessage")}
          >
            <SendLogo />
          </div>
        )}
      </ComposerShell>
    </>
  );
};

export default WidgetChatComposer;
